import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { ArrowIcon, CheckIcon } from "@/components/icons";

/* Content never stretches past 1200px, so wide screens keep the same proportions. */
const Wrap = ({ className = "", children, ...rest }) => (
  <div className={`mx-auto w-full max-w-[1200px] px-5 lg:px-10 ${className}`} {...rest}>
    {children}
  </div>
);

/* Section label. Marker highlight, the same effect as the hero phrase, so the labels read like the nav items. */
const Eyebrow = ({ children, className = "" }) => (
  <span className={`t-meta brush brush-label self-start text-accent-foreground ${className}`}>{children}</span>
);

const Pill = ({ children }) => (
  <span className="h-8 px-3.5 flex items-center rounded-full border border-foreground font-mono text-[13px]">{children}</span>
);

const Citation = ({ n = 1, children }) => (
  <div className="inline-flex items-center gap-2 h-7 px-3 rounded-full border border-foreground bg-white font-mono text-xs">
    <span className="font-medium">{n}</span>
    <span>{children}</span>
  </div>
);

const NavActions = () => {
  const { user } = useAuth();
  if (user) {
    return (
      <Link
        to="/dashboard"
        data-testid="nav-dashboard-link"
        className="paper-dark h-10 px-5 flex items-center gap-2 rounded-xl text-primary-foreground text-sm font-medium"
      >
        Go to dashboard <ArrowIcon size={16} />
      </Link>
    );
  }
  return (
    <Link
      to="/login"
      data-testid="nav-signin-link"
      className="paper-dark h-10 px-5 flex items-center rounded-xl text-primary-foreground text-sm font-medium"
    >
      Sign in
    </Link>
  );
};

const STEPS = [
  {
    n: "01",
    title: "Add your sources",
    body: "Drop in help docs, FAQs and onboarding guides. We split them into passages and index them while you watch.",
    aside: (
      <div className="flex flex-wrap gap-2 lg:justify-end">
        {[".md", ".txt", ".pdf", ".docx", ".csv"].map((e) => (
          <Pill key={e}>{e}</Pill>
        ))}
      </div>
    ),
  },
  {
    n: "02",
    title: "Paste one line",
    body: "Copy the script tag into your site. The chat widget appears in the corner and matches your page.",
    aside: (
      <div className="paper-dark rounded-xl px-5 py-4 text-primary-foreground font-mono text-[13px] leading-[1.6] overflow-hidden">
        <span className="text-accent">&lt;script</span> src="https://&lt;your-backend&gt;/api/widget.js"
        <br />
        &nbsp;&nbsp;data-firstrun-key="wk_••••••••"<span className="text-accent">&gt;&lt;/script&gt;</span>
      </div>
    ),
  },
  {
    n: "03",
    title: "Answers with receipts",
    body: "Visitors ask in plain language. Every reply comes from your content and links to the passage behind it.",
    aside: (
      <div className="flex lg:justify-end">
        <div className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-accent border border-foreground text-sm font-medium">
          <CheckIcon size={16} sw={1.75} /> Grounded in your documents only
        </div>
      </div>
    ),
  },
];

const FEATURES = [
  ["Private by default", "Each account's documents, chats and widget key are isolated from every other account."],
  ["Bring any format", "Markdown, text, PDF, Word and CSV files all work out of the box."],
  ["One script tag", "A lightweight embed with no build step. Regenerate the key any time."],
  ["Usage you can see", "Track monthly messages from the workspace before you ever hit a limit."],
];

export const Landing = () => {
  const [cycle, setCycle] = useState("monthly");
  const annual = cycle === "annual";

  return (
    <div className="min-h-screen text-foreground antialiased selection:bg-accent">
      {/* Nav */}
      <header data-testid="landing-header" className="border-b border-border">
        <Wrap className="h-16 flex items-center justify-between">
          <Logo to="/" size={28} textClass="text-2xl" />
          <nav className="hidden md:flex items-center gap-8 text-sm text-soft" aria-label="Sections">
            <a href="#how" className="brush-hover">How it works</a>
            <a href="#receipts" className="brush-hover">Citations</a>
            <a href="#pricing" className="brush-hover">Pricing</a>
          </nav>
          <NavActions />
        </Wrap>
      </header>

      {/* Hero */}
      <Wrap className="pt-10 pb-16 lg:pt-14 lg:pb-20 grid gap-12 lg:grid-cols-[1.05fr_1fr] items-center">
        <div className="flex flex-col gap-6">
          <h1 className="t-display">
            Your docs already have <span className="brush">the answer.</span>
          </h1>
          <p className="t-lead text-soft max-w-[480px] text-[19px]">
            Upload your help docs, paste one script tag, and your visitors get instant answers that point back to the
            exact passage they came from.
          </p>
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <Link
              to="/signup"
              data-testid="hero-start-free-button"
              className="paper-dark h-12 px-6 flex items-center gap-2 rounded-xl text-primary-foreground text-base font-medium"
            >
              Start free <ArrowIcon size={16} />
            </Link>
            <a
              href="#receipts"
              data-testid="hero-signin-button"
              className="h-12 px-5 flex items-center rounded-xl border border-foreground text-base font-medium"
            >
              See a cited answer
            </a>
          </div>
          <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
            <span>No credit card</span>
            <span>·</span>
            <span>30 free messages a month</span>
          </div>
        </div>

        <div className="relative lg:h-[440px]" aria-hidden="true">
          <div className="hidden lg:block paper absolute left-0 top-0 w-[400px] h-[380px] rounded-2xl border border-border overflow-hidden">
            <div className="h-10 border-b border-border flex items-center px-4 font-mono text-xs text-muted-foreground">
              yourapp.com/settings/api
            </div>
            <div className="p-6 flex flex-col gap-3">
              <div className="w-40 h-5 rounded-md bg-track" />
              <div className="w-full h-2.5 rounded-md bg-[#EAE5D7]" />
              <div className="w-[90%] h-2.5 rounded-md bg-[#EAE5D7]" />
              <div className="w-[68%] h-2.5 rounded-md bg-[#EAE5D7]" />
              <div className="paper-inset h-20 rounded-xl mt-2" />
            </div>
          </div>
          <div className="lg:absolute right-0 bottom-0 w-full max-w-[360px] rounded-2xl bg-white border border-foreground shadow-[6px_6px_0_hsl(var(--primary))] overflow-hidden">
            <div className="h-12 px-4 flex items-center gap-2.5 border-b border-border">
              <div className="w-2.5 h-2.5 rounded-full bg-success" />
              <span className="font-semibold text-sm">Docs assistant</span>
            </div>
            <div className="p-4 flex flex-col gap-3">
              <div className="paper-dark self-end max-w-[240px] px-3.5 py-2.5 rounded-[14px_14px_4px_14px] text-primary-foreground text-sm leading-[1.4]">
                How do I reset my API key?
              </div>
              <div className="paper-inset self-start px-3.5 py-3 rounded-[14px_14px_14px_4px] text-sm leading-normal">
                Open Settings, then API, and choose <span className="bg-accent px-[3px]">Regenerate key</span>. Your old
                key stops working right away.
                <div className="mt-2.5">
                  <Citation>api-guide.md · Rotating keys</Citation>
                </div>
              </div>
            </div>
            <div className="mx-4 mb-4 h-11 rounded-xl border border-border flex items-center px-3.5 text-sm text-muted-foreground">
              Ask anything about your product
            </div>
          </div>
        </div>
      </Wrap>

      {/* How it works */}
      <section id="how" data-testid="how-it-works">
        <Wrap className="py-16 lg:py-20 flex flex-col gap-10">
          <div className="flex flex-col gap-3 lg:flex-row lg:justify-between lg:items-end">
            <div className="flex flex-col gap-4">
              <Eyebrow>How it works</Eyebrow>
              <h2 className="t-h2 max-w-[560px]">Three steps. No build step.</h2>
            </div>
            <p className="t-lead text-soft max-w-[340px]">From a folder of documents to a live assistant in a few minutes.</p>
          </div>
          <div className="flex flex-col">
            {STEPS.map((s, i) => (
              <div
                key={s.n}
                className={`grid gap-4 lg:gap-8 lg:grid-cols-[88px_minmax(0,1fr)_minmax(0,1fr)] py-7 items-center border-t ${
                  i === 0 ? "border-foreground" : "border-border"
                } ${i === STEPS.length - 1 ? "border-b" : ""}`}
              >
                <div className="font-heading text-5xl leading-none">{s.n}</div>
                <div className="flex flex-col gap-2">
                  <h3 className="t-h3">{s.title}</h3>
                  <p className="t-body text-soft max-w-[400px]">{s.body}</p>
                </div>
                {s.aside}
              </div>
            ))}
          </div>
        </Wrap>
      </section>

      {/* Citations */}
      <section id="receipts" data-testid="features" className="paper-dark text-primary-foreground">
        <Wrap className="py-16 lg:py-20 flex flex-col gap-14">
          <div className="grid gap-10 lg:gap-14 lg:grid-cols-2 items-center">
            <div className="flex flex-col gap-4">
              <Eyebrow>Citations</Eyebrow>
              <h2 className="t-h2">Every answer shows where it came from.</h2>
              <p className="t-lead text-[#D8D4C8] max-w-[460px]">
                The assistant answers only from what you uploaded. Each reply links to the source document, and the
                passage it used is highlighted so your team can check it in seconds.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <div className="bg-white text-foreground rounded-2xl p-6 flex flex-col gap-3">
                <div className="flex justify-between font-mono text-xs text-muted-foreground">
                  <span>api-guide.md</span>
                  <span>Rotating keys</span>
                </div>
                <div className="text-base leading-[1.65] text-soft">
                  API keys do not expire on their own. To rotate a key, open Settings, then API.{" "}
                  <span className="bg-accent text-foreground px-[3px] py-0.5">
                    Choose Regenerate key and confirm. The previous key is revoked immediately
                  </span>{" "}
                  and any requests using it will return 401.
                </div>
              </div>
              <div className="self-center w-0.5 h-4 bg-accent" />
              <div className="rounded-2xl bg-white text-foreground px-6 py-5 flex flex-col gap-3">
                <div className="text-base leading-[1.55]">
                  Choose <b className="font-semibold">Regenerate key</b> under Settings, then API. The old key is
                  revoked immediately.
                </div>
                <div className="self-start">
                  <Citation>api-guide.md</Citation>
                </div>
              </div>
            </div>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 pt-10 border-t border-soft">
            {FEATURES.map(([t, d]) => (
              <div key={t} className="flex flex-col gap-1.5">
                <div className="text-base font-semibold">{t}</div>
                <div className="text-sm leading-[1.55] text-[#D8D4C8]">{d}</div>
              </div>
            ))}
          </div>
        </Wrap>
      </section>

      {/* Pricing */}
      <section id="pricing" data-testid="pricing">
        <Wrap className="py-16 lg:py-20 flex flex-col items-center gap-8">
          <div className="flex flex-col items-center gap-3 text-center">
            <Eyebrow className="self-center">Pricing</Eyebrow>
            <h2 className="t-h2">Start free. Pay when you grow.</h2>
            <p className="t-lead text-soft">Upgrade to unlimited messages whenever you are ready.</p>
          </div>
          <div data-testid="pricing-cycle-toggle" className="flex p-1 gap-1 rounded-xl border border-foreground">
            {[
              ["monthly", "Monthly", "pricing-cycle-monthly"],
              ["annual", "Annual · 2 months free", "pricing-cycle-annual"],
            ].map(([id, label, tid]) => (
              <button
                key={id}
                data-testid={tid}
                onClick={() => setCycle(id)}
                className={`h-10 px-4 sm:px-5 rounded-lg text-sm font-medium ${
                  cycle === id ? "paper-dark text-primary-foreground" : "text-foreground"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="grid gap-6 w-full max-w-[880px] md:grid-cols-2">
            <div className="paper rounded-2xl border border-border p-8 flex flex-col gap-5">
              <div className="flex flex-col gap-1">
                <div className="t-h3">Free</div>
                <div className="text-sm text-soft">To try it on a small site.</div>
              </div>
              <div className="font-heading text-[56px] leading-none tracking-[-0.02em]">
                $0<span className="font-sans text-sm tracking-normal text-muted-foreground"> /month</span>
              </div>
              <ul className="flex flex-col gap-3 text-[15px]">
                {["Up to 30 messages a month", "1 embeddable widget", "Answers with citations", "Docs and usage workspace"].map((t) => (
                  <li key={t} className="flex gap-3 items-center">
                    <CheckIcon size={16} className="text-success" sw={2} /> {t}
                  </li>
                ))}
              </ul>
              <Link
                to="/signup"
                data-testid="pricing-free-cta"
                className="mt-1 h-12 flex items-center justify-center rounded-xl border border-foreground text-base font-medium"
              >
                Choose Free
              </Link>
            </div>
            <div className="paper-dark rounded-2xl text-primary-foreground p-8 flex flex-col gap-5">
              <div className="flex justify-between items-start">
                <div className="flex flex-col gap-1">
                  <div className="t-h3">Pro</div>
                  <div className="text-sm text-[#D8D4C8]">For products with real traffic.</div>
                </div>
                <span className="h-6 px-2.5 flex items-center rounded-full bg-accent text-accent-foreground font-mono text-xs">
                  Most popular
                </span>
              </div>
              <div data-testid="pro-price">
                {annual ? (
                  <div className="flex flex-col gap-1">
                    <div className="font-heading text-[56px] leading-none tracking-[-0.02em]">
                      $290<span className="font-sans text-sm tracking-normal text-[#D8D4C8]"> /year</span>
                    </div>
                    <div className="text-sm text-accent">About $24.17 a month. Save $58 a year.</div>
                  </div>
                ) : (
                  <div className="font-heading text-[56px] leading-none tracking-[-0.02em]">
                    $29<span className="font-sans text-sm tracking-normal text-[#D8D4C8]"> /month</span>
                  </div>
                )}
              </div>
              <ul className="flex flex-col gap-3 text-[15px]">
                {["Unlimited messages", "Priority answer quality", "Higher widget traffic", "Everything in Free"].map((t) => (
                  <li key={t} className="flex gap-3 items-center">
                    <CheckIcon size={16} className="text-accent" sw={2} /> {t}
                  </li>
                ))}
              </ul>
              <Link
                to="/signup"
                data-testid="pricing-pro-cta"
                className="mt-1 h-12 flex items-center justify-center rounded-xl bg-accent text-accent-foreground text-base font-semibold"
              >
                Choose Pro
              </Link>
            </div>
          </div>
        </Wrap>
      </section>

      {/* CTA */}
      <Wrap className="pb-14 lg:pb-16">
        <div className="paper rounded-2xl border border-border max-w-[880px] mx-auto px-6 py-6 lg:px-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="font-heading text-2xl lg:text-3xl leading-tight tracking-[-0.02em]">
            Put your docs to work today.
          </h2>
          <Link
            to="/signup"
            data-testid="cta-band-start-free"
            className="group inline-flex items-center gap-3 self-start sm:self-auto shrink-0 text-2xl lg:text-3xl font-heading"
          >
            <span className="brush">Create your workspace</span>
            <ArrowIcon size={20} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </Wrap>

      {/* Footer */}
      <footer data-testid="landing-footer" className="border-t border-border">
        <Wrap className="py-6 flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2.5">
            <span className="font-heading text-xl text-foreground">firstrun</span>
            <span className="font-mono text-xs">© {new Date().getFullYear()}</span>
          </div>
          <div className="flex gap-6">
            <Link to="/login" data-testid="footer-signin-link">Sign in</Link>
          </div>
        </Wrap>
      </footer>
    </div>
  );
};
